"use client";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PricingPlans } from './sections/pricing-plans';
import { CartContainer, PRODUCT_ORDER, PRODUCT_DESCRIPTIONS, CartItem } from './cart/cart-container';
import { useAppSelector } from '@/app/store/hooks';
import {
    useGetProductsQuery,
    useGetCartQuery,
    useUpdateCartItemMutation,
    useRemoveCartItemMutation,
    useClearCartMutation,
} from '@/app/store/api/cartApi';
import type { ProductType, Product, CartData } from '@/types/cart.types';
import type { Currency } from '@/app/store/slices/currencySlice';

// Maps ProductType → pricing tab
const DECK_TYPES: ProductType[] = ['PRO_DECK', 'FAST_DECK', 'FUNDRAISING_PITCH_DECK'];

const PricingPage = () => {
    const [activeTab, setActiveTab] = useState<'decks' | 'documents'>('decks');
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const selectedCurrency = useAppSelector((state) => state.currency.selectedCurrency);
    const [isLoginOpen, setIsLoginOpen] = useState(false);

    // ── API hooks ─────────────────────────────────────────────────
    const { data: productsData, isLoading: productsLoading } = useGetProductsQuery(selectedCurrency);
    const {
        data: cartData,
        isLoading: cartLoading,
    } = useGetCartQuery(selectedCurrency, { skip: !isAuthenticated });

    const [updateCartItem] = useUpdateCartItemMutation();
    const [removeCartItem] = useRemoveCartItemMutation();
    const [clearCart] = useClearCartMutation();

    // ── Clear cart on currency change ─────────────────────────────
    useEffect(() => {
        if (isAuthenticated && cartData?.data?.currency && cartData.data.currency !== selectedCurrency) {
            console.log('[Cart] Currency mismatch detected, clearing cart automatically');
            clearCart();
        }
    }, [selectedCurrency, cartData, isAuthenticated, clearCart]);

    // ── Local quantity map (used for unauthenticated preview & optimistic UI) ───
    const [localQuantities, setLocalQuantities] = useState<Record<ProductType, number>>({
        PRO_DECK: 0,
        FAST_DECK: 0,
        FUNDRAISING_PITCH_DECK: 0,
        DOCUMENT: 0,
        MARKET_RESEARCH: 0,
    });

    // Sync local quantities from server cart when it arrives
    useEffect(() => {
        if (!cartData?.data) return;
        const serverMap: Record<string, number> = {};
        cartData.data.items.forEach((item) => {
            serverMap[item.productType] = item.quantity;
        });
        setLocalQuantities((prev) => {
            const next = { ...prev };
            PRODUCT_ORDER.forEach((type: ProductType) => {
                next[type] = serverMap[type] ?? 0;
            });
            return next;
        });
    }, [cartData]);

    const pendingRef = useRef<Record<ProductType, ReturnType<typeof setTimeout>>>({} as any);

    const handleUpdateQuantity = useCallback(async (id: ProductType, delta: number) => {
        if (!isAuthenticated) {
            setIsLoginOpen(true);
            return;
        }

        const currentQty = localQuantities[id] ?? 0;
        const newQty = Math.max(0, currentQty + delta);

        // Optimistic update
        setLocalQuantities((prev) => ({ ...prev, [id]: newQty }));

        if (pendingRef.current[id]) clearTimeout(pendingRef.current[id]);

        pendingRef.current[id] = setTimeout(async () => {
            try {
                if (newQty === 0) {
                    await removeCartItem(id).unwrap();
                } else {
                    await updateCartItem({ productType: id, quantity: newQty, currency: selectedCurrency }).unwrap();
                }
            } catch (err) {
                setLocalQuantities((prev) => ({ ...prev, [id]: currentQty }));
                console.error('[Cart] update failed, reverting', err);
            }
        }, 300);
    }, [isAuthenticated, localQuantities, updateCartItem, removeCartItem]);

    const handleSeeFeatures = (id: string) => {
        const productType = id as ProductType;
        if (DECK_TYPES.includes(productType)) {
            setActiveTab('decks');
        } else {
            setActiveTab('documents');
        }

        setTimeout(() => {
            const section = document.getElementById('pricing-plans-section');
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 50);
    };

    const handleAddToCart = async (productType: ProductType) => {
        // Add 1 to current quantity or set to 1
        await handleUpdateQuantity(productType, 1);

        // Scroll to checkout summary
        setTimeout(() => {
            const summaryElement = document.getElementById('cart-order-summary');
            if (summaryElement) {
                summaryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                // Fallback to cart section
                const cartSection = document.getElementById('cart-section');
                if (cartSection) {
                    cartSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 100);
    };

    // ── Build products and items for CartContainer ────────────────
    const products: Product[] = productsData?.data?.products ?? [];
    const sortedProducts = PRODUCT_ORDER.map((type: ProductType) => products.find((p) => p.productType === type)).filter(Boolean) as Product[];

    // Prioritize local symbol mapping to ensure correct display even if backend returns default symbol
    const localSymbolMap: Record<Currency, string> = { INR: '₹', USD: '$', EUR: '€' };
    const currencySymbol = localSymbolMap[selectedCurrency as Currency] || productsData?.data?.currencySymbol || '₹';

    const cartItems: CartItem[] = sortedProducts.map((product) => ({
        id: product.productType,
        name: product.name,
        price: product.price,
        currencySymbol: currencySymbol,
        quantity: localQuantities[product.productType] ?? 0,
        description: product.subtitle ?? PRODUCT_DESCRIPTIONS[product.productType],
    }));

    return (
        <div className="bg-white min-h-screen pb-4 pt-20 font-sans selection:bg-blue-100 selection:text-blue-900">
            <CartContainer 
                onSeeFeatures={handleSeeFeatures} 
                cartItems={cartItems}
                cartData={cartData?.data}
                cartLoading={isAuthenticated && cartLoading}
                productsLoading={productsLoading}
                onUpdateQuantity={handleUpdateQuantity}
                isLoginOpen={isLoginOpen}
                setIsLoginOpen={setIsLoginOpen}
            />
            <PricingPlans 
                activeTab={activeTab} 
                setActiveTab={setActiveTab} 
                onAddToCart={handleAddToCart}
            />
        </div>
    );
};

export default PricingPage;
